/*
  PULSE scanning floor and colour cycle (<pulse-hero-scan>), used by both home heroes: "Calmer 2"
  (sections/pulse-hero-scan.liquid, owner update 8) and the lineup hero with its picture on
  (sections/pulse-hero-lineup.liquid, owner update 9). After every scan loop the tester changes colour,
  Blue -> Gray -> Yellow -> Red -> Blue, and a colour name under it (if the hero has them) lights up in step.

  - The loop is the board's CSS animation (assets/pulse-scan.css, 10 s); its animationiteration event changes the
    colour, so the change always comes right after a scan ends.
  - Only Blue loads with the page. The other three get their src after the page's load event. A colour is ready once
    its image has loaded (the img load event) or decoded (decode()), so a rejected decode() alone does not lose it. The
    cycle skips a colour that is not ready (an image that failed to load) and goes on with the next ready one: never a
    blank tester, and the cycle never stops for good. With no other colour ready, the current one stays.
  - Off-screen (IntersectionObserver) or in a hidden tab, the scan pauses (data-paused) and with it the colour change.
    With a single picture (the lineup hero's colour cycle off) the scan still pauses; the picture stays.
  - Reduced motion or the section's animation setting off (data-animate="false"): nothing runs, Blue stays.
*/
if (!customElements.get('pulse-hero-scan')) {
  class PulseHeroScan extends HTMLElement {
    /** The next colour after `index` in the cycle whose image is ready (not-ready ones skipped); null if none is. */
    static nextIndex(index, ready) {
      for (let step = 1; step < ready.length; step++) {
        const next = (index + step) % ready.length;
        if (ready[next]) return next;
      }
      return null;
    }

    connectedCallback() {
      this.images = Array.from(this.querySelectorAll('.pulse-hs__img'));
      this.names = Array.from(this.querySelectorAll('.pulse-hs__colour'));
      this.loop = this.querySelector('.pulse-hs__scanned');
      // kept when the element is moved (disconnect + connect): its images stay loaded and the colour shown stays
      if (this.index === undefined) this.index = 0;
      if (!this.ready) this.ready = this.images.map((image, i) => i === 0);
      this.visible = true;
      if (this.dataset.animate === 'false' || !this.loop) return;

      this.running = true;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onIteration = this.onIteration.bind(this);
      this.onVisibility = this.updatePaused.bind(this);
      this.onMotion = this.onMotion.bind(this);
      this.onLoad = this.loadImages.bind(this);

      this.addEventListener('animationiteration', this.onIteration);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.motion.addEventListener('change', this.onMotion);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.visible = entries[entries.length - 1].isIntersecting;
          this.updatePaused();
        });
        this.observer.observe(this);
      }
      this.updatePaused();

      if (document.readyState === 'complete') this.loadImages();
      else window.addEventListener('load', this.onLoad, { once: true });
    }

    /** Removes what connectedCallback added; nothing to do when it returned early (Animate off, no scan layer). */
    disconnectedCallback() {
      if (!this.running) return;
      this.running = false;
      this.removeEventListener('animationiteration', this.onIteration);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.motion.removeEventListener('change', this.onMotion);
      window.removeEventListener('load', this.onLoad);
      this.observer?.disconnect();
    }

    updatePaused() {
      this.toggleAttribute('data-paused', document.hidden || !this.visible);
    }

    /** Sets the src of the colours not loaded yet (not under reduced motion: they would never show). */
    loadImages() {
      if (this.motion.matches) return;
      this.images.forEach((image, i) => {
        if (i === 0 || !image.dataset.src) return;
        const markReady = () => {
          this.ready[i] = true;
        };
        // listen first, then set the src: a cached image may load at once
        image.addEventListener('load', markReady, { once: true });
        image.srcset = image.dataset.srcset;
        image.src = image.dataset.src;
        delete image.dataset.src;
        image.decode().then(markReady).catch(() => {});
      });
    }

    onIteration(event) {
      if (event.target !== this.loop) return;
      const next = PulseHeroScan.nextIndex(this.index, this.ready);
      if (next !== null) this.show(next);
    }

    onMotion() {
      if (this.motion.matches) this.show(0);
      else this.loadImages();
    }

    show(index) {
      this.index = index;
      this.images.forEach((image, i) => {
        image.classList.toggle('is-current', i === index);
        if (i === index) image.removeAttribute('aria-hidden');
        else image.setAttribute('aria-hidden', 'true');
      });
      this.names.forEach((name, i) => name.classList.toggle('is-current', i === index));
    }
  }

  customElements.define('pulse-hero-scan', PulseHeroScan);
}
